import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { IS_NATIVE, getAppStripeNonce, clearAppStripeNonce } from "../lib/stripeFlow";

// Cold-start half of the Stripe sheet-close self-completion (the stranded-
// signup bug): an app killed mid-Stripe-onboarding relaunches with NO route
// back into the round trip — the onboarding draft was wiped before the
// redirect — but the app still holds its completion key. Unauthenticated +
// stashed nonce → run the callback machinery, which silently logs the talent
// in (Stripe finished), silently re-enters Stripe (link expired), or shows
// the friendly Almost-there card (genuinely unfinished). Authenticated with a
// leftover stash → the stash is stale; just tidy up.
//
// ONCE PER BOOT (build 41, the "Log in to finish" loop): this used to re-run on
// every route change. The app keeps its stash for 24h but the server purges
// its copy after 60 min, so a talent who closed Stripe last night and opened
// the app today got `needs_login` — and then every tap that left the callback
// page ("Log in to finish" → /login, Back → home) re-fired this and dragged
// them straight back. Nothing on screen could ever move. One attempt per cold
// start is the whole job; the callback page owns what happens after.
const StripeSelfCompletion: React.FC = () => {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!IS_NATIVE || loading || attemptedRef.current) return;
    attemptedRef.current = true;
    const nonce = getAppStripeNonce();
    if (!nonce) return;
    if (user) {
      clearAppStripeNonce();
      return;
    }
    // Already inside the round trip (deep link / sheet-close got there first).
    if (location.pathname.startsWith("/register/stripe/")) return;
    navigate(`/register/stripe/callback?nonce=${encodeURIComponent(nonce)}`, { replace: true });
  }, [loading, user, location.pathname, navigate]);

  return null;
};

export default StripeSelfCompletion;
